import React from 'react';
import {observer} from 'mobx-react';
import {DefaultModalLayout} from '../../../../../../components/modal/layouts/default-modal-layout';
import {BUTTON_VARIANTS} from '../../../../../../components/inputs/button-variants';
import FontBody1 from '../../../../../../components/fonts/font-body1/font-body1';
import FontBody2 from '../../../../../../components/fonts/font-body2/font-body2';
import {FONT_COLOR, FONT_VARIANT} from '../../../../../../components/fonts/font-props';
import CheckboxesTreeDropdown
    from '../../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree-dropdown';
import Label, {LABEL_VARIANT} from '../../../../../../components/label/label';
import {Note, NOTE_TYPE} from '../../../../../../components/note/note';
import useDashboardTranslation from '../../../../../../dashboard-translation/hooks/dashboard-translation-hook';
import ExchangeLocationsRuleModalStore from '../../stores/exchange-locations-rule-modal-store';
import './exchange-locations-rule-popup.scss';
import {isSet} from "../../../../../../models/model-types-methods";

interface IExchangeLocationsRulePopupProps {
    onClose: () => void;
    onApply: () => void;
    modalStore: ExchangeLocationsRuleModalStore;
}

/**
 * Modal for adding/editing exchange locations rules.
 * Allows selecting warehouse locations and corresponding zones/countries.
 */
const ExchangeLocationsRulePopup: React.FC<IExchangeLocationsRulePopupProps> = observer(
    (
        {
            onClose,
            onApply,
            modalStore
        } : IExchangeLocationsRulePopupProps
    ) => {
        const {getTranslation} = useDashboardTranslation();
        const pageScope = 'dashboard-settings-store';

        const locationExceptionsPopupHeaderText = getTranslation('LOCATION_EXCEPTIONS_POPUP_HEADER', pageScope);
        const locationExceptionsPopupNoteText = getTranslation('LOCATION_EXCEPTIONS_POPUP_NOTE', pageScope);
        const locationExceptionsPopupSelectWarehouseLocationText = getTranslation('LOCATION_EXCEPTIONS_POPUP_SELECT_WAREHOUSE_LOCATION', pageScope);
        const locationExceptionsPopupSelectZonesText = getTranslation('LOCATION_EXCEPTIONS_POPUP_SELECT_ZONES', pageScope);
        const locationExceptionsPopupFallbackNoteText = getTranslation('LOCATION_EXCEPTIONS_POPUP_FALLBACK_NOTE', pageScope);
        const locationExceptionsPopupCancelText = getTranslation('LOCATION_EXCEPTIONS_POPUP_CANCEL', pageScope);
        const locationExceptionsPopupApplyText = getTranslation('LOCATION_EXCEPTIONS_POPUP_APPLY', pageScope);

        return (
            <DefaultModalLayout
                isCloseDisabled={false}
                onCloseClicked={onClose}
                buttons={[
                    {
                        variant: BUTTON_VARIANTS.Tertiary,
                        text: locationExceptionsPopupCancelText,
                        onClick: onClose
                    },
                    {
                        variant: BUTTON_VARIANTS.Primary,
                        text: locationExceptionsPopupApplyText,
                        onClick: onApply,
                        disabled: !modalStore.canApply()
                    }
                ]}
            >
                <>
                    <div className="exchange-locations-rule-popup-header">
                        <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                            {locationExceptionsPopupHeaderText}
                        </FontBody1>
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>
                            {locationExceptionsPopupNoteText}
                        </FontBody1>
                    </div>
                    <div className={`exchange-locations-rule-popup ${modalStore.isFallbackMode() ? 'fallback' : ''} `}>

                    <div className="exchange-locations-rule-popup-field">
                        <div className="exchange-locations-rule-popup-field-label">
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                {!modalStore.isFallbackMode() && '1. '}{locationExceptionsPopupSelectWarehouseLocationText}
                            </FontBody2>
                        </div>

                        {modalStore.getWarehousesLabels().length > 0 && (
                            <div className="exchange-locations-rule-popup-field-selected">
                                {modalStore.getWarehousesLabels().map((item) => (
                                    <Label
                                        key={item.value}
                                        variant={LABEL_VARIANT.DARK_BLUE}
                                        labelText={item.label}
                                    />
                                ))}
                            </div>
                        )}

                        <CheckboxesTreeDropdown
                            options={modalStore.getWarehousesOptions()}
                            value={modalStore.getWarehousesValue()}
                            onChange={modalStore.onWarehousesChanged}
                            searchInputPlaceholder="Select"
                            compressed={modalStore.isFallbackMode()}
                            checkboxesSelectAllText={'Select all'}
                        />
                    </div>

                    {/** Zones selection (visible after warehouse is selected, hidden for fallback mode) */}
                    {!modalStore.isFallbackMode() && (
                        <div className="exchange-locations-rule-popup-field">
                            <div className="exchange-locations-rule-popup-field-label">
                                <FontBody2 variant={FONT_VARIANT.BOLD}>
                                    2. {locationExceptionsPopupSelectZonesText}
                                </FontBody2>
                            </div>

                            {isSet(modalStore.getZonesValue()) && modalStore.getZonesValue()!.length > 0 && (
                                <div className="exchange-locations-rule-popup-field-selected">
                                    {modalStore.getZonesValue()!.map((item) => (
                                        <Label
                                            key={item.value}
                                            variant={LABEL_VARIANT.ACCENT_LIGHT}
                                            labelText={item.label}
                                        />
                                    ))}
                                </div>
                            )}

                            <CheckboxesTreeDropdown
                                options={modalStore.getZonesOptions()}
                                value={modalStore.getZonesValue()}
                                onChange={modalStore.onZonesChanged}
                                searchInputPlaceholder="Select"
                                checkboxesSelectAllText={'Select all'}
                                compressed={false}
                            />
                        </div>
                    )}
                    { modalStore.isFallbackMode() &&
                        <div className="exchange-locations-rule-popup-field-fallback-note">
                            <Note type={NOTE_TYPE.INFORMATION} body={locationExceptionsPopupFallbackNoteText} />
                        </div>
                    }
                </div>
                </>
            </DefaultModalLayout>
        );
    }
);

export default ExchangeLocationsRulePopup;

